import { AnalyticsAttributesProvider, Container, Row } from "../../components";
import { BreakpointProperties, isColumnProperties } from "../../components";
import { UiColumnModel, UiSectionModel } from "../../models";
import { renderViewModels } from "../../render-view-model";
import {
    BackgroundField,
    BackgroundFieldValue,
    Breakpoint,
    breakpoints,
    getColorsFromBackgroundField
} from "../../styles";

import { Section } from "./UiSection.styles";

type UiSectionProps = {
    readonly model: UiSectionModel;
};

const UiSection = ({ model }: UiSectionProps) => {
    const {
        title,
        columns = [],
        background = null,
        elementId,
        sectionBackground,
        verticalAlign
    } = model.content;

    const hasSectionBackground =
        Array.isArray(sectionBackground) && sectionBackground.length > 0;

    const shouldUseInlineBackgroundFallback =
        !hasSectionBackground &&
        !!background &&
        (Boolean((background as BackgroundFieldValue).color) ||
            Boolean((background as BackgroundFieldValue).gradient));

    const fallbackColors = shouldUseInlineBackgroundFallback
        ? getColorsFromBackgroundField(background as BackgroundFieldValue)
        : {};

    const sectionStyle = shouldUseInlineBackgroundFallback
        ? {
              background: fallbackColors.background,
              color: fallbackColors.color
          }
        : undefined;

    return (
        <Section
            id={elementId}
            className="ui-section"
            overflowingBreakpoints={getOverflowingBreakpoints(columns)}
            sectionBackground={sectionBackground as BackgroundField[]}
            background={background as BackgroundFieldValue}
            style={sectionStyle}
        >
            <Container className="ui-section__container">
                <Row className="ui-section__row" verticalAlign={verticalAlign}>
                    <AnalyticsAttributesProvider
                        value={{
                            eventContainer: "uiSection",
                            labelEnglish: title
                        }}
                    >
                        {renderViewModels(columns)}
                    </AnalyticsAttributesProvider>
                </Row>
            </Container>
        </Section>
    );
};

//determine which breakpoints (xs, sm, etc) overflow the 12 column grid
//verbose to avoid ts error "type 'string' can't be used to index type ColumnProperties"
function getOverflowingBreakpoints(columns: readonly UiColumnModel[]) {
    const breakpointKeys = Object.keys(breakpoints) as Breakpoint[];

    return columns.reduce<Breakpoint[]>((overflowingBreakpoints, column) => {
        const { properties } = column.content;
        if (isColumnProperties(properties)) {
            breakpointKeys
                .filter(
                    (breakpoint) =>
                        !overflowingBreakpoints.includes(breakpoint) &&
                        (propertyHasNegativeOffset(properties[breakpoint]) ||
                            propertyHasNegativePush(properties[breakpoint]) ||
                            propertyExceedsSpan(properties[breakpoint]))
                )
                .forEach((breakpoint) =>
                    overflowingBreakpoints.push(breakpoint)
                );
        }
        return overflowingBreakpoints;
    }, []);
}

function propertyHasNegativeOffset(property?: BreakpointProperties) {
    return property?.offset && parseInt(property.offset) < 0;
}

function propertyHasNegativePush(property?: BreakpointProperties) {
    return property?.push && parseInt(property.push) < 0;
}

function propertyExceedsSpan(property?: BreakpointProperties) {
    if (!property) return false;
    const span = property.span ? parseInt(property.span) : 0;
    const push = property.push ? parseInt(property.push) : 0;
    const offset = property.offset ? parseInt(property.offset) : 0;
    return span + push + offset > 12;
}

export default UiSection;
